import { Users } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';

interface LetterAvatarProps {
  name?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  showFallback?: boolean;
}

/**
 * High-contrast palette (26 colours) chosen so white text (#fff) passes WCAG AA
 * for small text. Letters are mapped deterministically (A→0, B→1 …) and wrap
 * modulo the palette length, so each initial always shows the same colour
 * regardless of session or device.
 */
const palette: string[] = [
  'bg-indigo-600', // A
  'bg-rose-600', // B
  'bg-amber-700', // C
  'bg-emerald-600', // D
  'bg-sky-700', // E
  'bg-purple-700', // F
  'bg-pink-700', // G
  'bg-blue-700', // H
  'bg-fuchsia-700', // I
  'bg-teal-700', // J
  'bg-red-700', // K
  'bg-cyan-700', // L
  'bg-violet-700', // M
  'bg-lime-700', // N
  'bg-orange-700', // O
  'bg-stone-700', // P
  'bg-emerald-700', // Q
  'bg-indigo-700', // R
  'bg-rose-700', // S
  'bg-amber-800', // T
  'bg-green-700', // U
  'bg-sky-800', // V
  'bg-purple-800', // W
  'bg-pink-800', // X
  'bg-blue-800', // Y
  'bg-fuchsia-800', // Z
];

const getColorForLetter = (letter: string): string => {
  const idx = (letter.toUpperCase().charCodeAt(0) - 65) % palette.length;
  return palette[idx] || 'bg-neutral-600';
};

// Generate initials from name
const getInitials = (name: string): string => {
  const words = name.trim().split(/\s+/);

  if (words.length >= 2) {
    return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
  }

  if (words[0]) {
    return words[0].charAt(0).toUpperCase();
  }

  return '?';
};

// Size variants
const sizeVariants = {
  xs: 'h-4 w-4 text-[10px]',
  sm: 'h-6 w-6 text-xs',
  md: 'h-8 w-8 text-sm',
  lg: 'h-10 w-10 text-base',
  xl: 'h-12 w-12 text-lg',
} as const;

const iconSizeVariants = {
  xs: 'h-2 w-2',
  sm: 'h-3 w-3',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
  xl: 'h-6 w-6',
} as const;

export const LetterAvatar = React.forwardRef<HTMLDivElement, LetterAvatarProps>(
  ({ name, size = 'md', className, showFallback = true, ...props }, ref) => {
    // If no name provided and showFallback is true, show unassigned state
    if (!name || name.trim() === '') {
      if (showFallback) {
        return (
          <div
            ref={ref}
            className={cn(
              'rounded-full bg-neutral-200 flex items-center justify-center flex-shrink-0',
              sizeVariants[size],
              className
            )}
            {...props}
          >
            <Users className={cn('text-neutral-500', iconSizeVariants[size])} />
          </div>
        );
      }
      return null;
    }

    const initials = getInitials(name);
    const bgColor = getColorForLetter(initials.charAt(0));

    return (
      <div
        ref={ref}
        className={cn(
          'rounded-full flex items-center justify-center font-medium flex-shrink-0 text-white select-none',
          sizeVariants[size],
          bgColor,
          className
        )}
        {...props}
      >
        {initials}
      </div>
    );
  }
);

LetterAvatar.displayName = 'LetterAvatar';
